/**
 * Definition of data formats for ports.
 */

import Opaque from 'ts-opaque'
import { DeviceHwID, ID, Port, TimeString } from './models'
import { Parity, BaudRate, SolarPortSolisConfigParams, SolarPortStecaGridConfigParams, SolarPortSunspecConfigParams, SolarPortRelayedConfigParams } from './solar'

/**
 * A combined string containing slash separated module and instance numbers like '3/0'.
 */
export type PortNumber = Opaque<string, 'PortNumber'>

/**
 * Types of ports.
 */
export type PortType =
  'BATTERY_OPTIMIZER' |
  'BATTERY_OPTIMIZER2' |
  'BATTERY_OPTIMIZER_SOC_PLAN' |
  'CLOCK' |
  'DIAGNOSTICS' |
  'EM_3PH' |
  'EM_HAN' |
  'EM_SIMPLE' |
  'EM_NETSUM' |
  'ENERGY_STORAGE' |
  'EVSE' |
  'FW_INFO' |
  'GPIO' |
  'HEAT_PUMP' |
  'OPTIMIZER_PRICE' |
  'OPTIMIZER_CONTROL' |
  'OPTIMIZER_WEATHER' |
  'OPTIMIZER' |
  'RADIO' |
  'RELAY' |
  'RS485' |
  'SCHEDULER' |
  'SOLAR' |
  'TEMPERATURE'

export type DestinationKind = 'DEST_KIND_THIS_DEVICE' | 'DEST_KIND_RADIO_SLAVE' | 'DEST_KIND_SERVER'

// Battery optimizer

export type BatteryState = 'BATTERY_OPTIMIZER_STATE_IDLE' | 'BATTERY_OPTIMIZER_STATE_CHARGE' | 'BATTERY_OPTIMIZER_STATE_AVOID_SELL' | 'BATTERY_OPTIMIZER_STATE_NET_ZERO' | 'BATTERY_OPTIMIZER_STATE_SELL' | 'BATTERY_OPTIMIZER_STATE_OVERRIDE' | 'BATTERY_OPTIMIZER_STATE_CHARGE_AND_PROTECT'
export type ReserveSate = 'BATOPT_EVENT_RESERVE_UNKNOWN' | 'BATOPT_EVENT_RESERVE_NO' | 'BATOPT_EVENT_RESERVE_PARTIAL' | 'BATOPT_EVENT_RESERVE_FULL'
export interface OptimizerEvent {
  state: BatteryState
  reserve_dn: ReserveSate
  reserve_up: ReserveSate
}

export type OptimizerEvents = [TimeString, OptimizerEvent][]

export interface PortValuesBatteryOptimizer {
  commissioning_errcode: number
  commissioning_state: number
  events: OptimizerEvents
}

export interface PortConfigBatteryOptimizer {
  enabled: boolean
  /** Always true when flashed: the v2 conf layout (16 W power units, state penalties). */
  new_conf?: boolean
  time_constant: number
  battery_port: PortNumber
  battery_dest: number
  battery_dest_kind: DestinationKind
  // Legacy (new_conf=0) fields — dead since the v2 layout, still reported by
  // devices that have not been re-flashed yet.
  n_charge_hours?: number
  charge_hour_soc?: number
  n_self_use_hours?: number
  n_sell_hours?: number
  charge_hour_power?: number
  /** W, floored to 16 W on the wire. */
  max_charge_power: number
  /** W, floored to 16 W on the wire. */
  max_discharge_power: number
  /** Hard-coded to 100. */
  max_soc: number
  min_soc: number
  // State penalty offsets, int8 (+127 = prevent the state). See utils/batopt-tuning.ts.
  penalty_pbs?: number
  penalty_pb?: number
  penalty_buy?: number
  penalty_sell?: number
}

// Battery optimizer 2.0

export type BatteryState2 =
  | 'BATOPT_STATE_RELEASE'
  | 'BATOPT_STATE_OFF'
  | 'BATOPT_STATE_BUY'
  | 'BATOPT_STATE_PREVENT_SELL'
  | 'BATOPT_STATE_PREVENT_BUY_SELL'
  | 'BATOPT_STATE_PREVENT_BUY'
  | 'BATOPT_STATE_SELL'
  // A slot state the protocol does not define (wire values 7-15); see Utils.Code.to_batopt2_state/1.
  | 'BATOPT_STATE_UNKNOWN'

export interface OptimizerEvent2 {
  state: BatteryState2
  duration_s: number
  is_user_override: boolean
}

export type OptimizerEvents2 = [TimeString, OptimizerEvent2][]

export interface PortValuesBatteryOptimizer2 {
  base_ts: TimeString
  timestep: number
  max_state_of_energy_wh: number
  dbg_worst_duration: number
  is_protecting_fuses: boolean
  // Which multiopt message sets the device received (fw44+; all false before).
  // Follower side: leader_rt (heartbeat fresh), slot_axis, forecast,
  // battery_plan; leader side: follower_rt, relay_powers.
  multiopt_link?: {
    bits: number
    leader_rt: boolean
    slot_axis: boolean
    forecast: boolean
    battery_plan: boolean
    follower_rt: boolean
    relay_powers: boolean
  }
  planning_state: number
  events: OptimizerEvents2
}

export interface PortConfigBatteryOptimizer2 {
  enabled: boolean
  battery_port: PortNumber
  battery_dest: number
  battery_dest_kind: DestinationKind
  min_soc: number
  max_soc: number
}

// Battery optimizer SoC plan (aux trajectory aligned 1:1 with batopt2 events)

export interface PortValuesBatteryOptimizerSocPlan {
  base_ts: TimeString
  soc_pct: number[]
}

export interface PortConfigBatteryOptimizerSocPlan {
}

// Clock

export interface PortValuesClock {
  clock: TimeString
  boot?: true
}

export interface PortConfigClock {
}

// Em simple.

export interface PortValuesEmSimple {
  energy: number
  power: number
}

export interface PortConfigEmSimple {
  enabled: boolean
  to_summer: boolean
  joules_per_blink: number
}

// Em HAN

export interface PortValuesEmHan {
  energy: number
  power: number
  cur_current: [number, number, number]
  phases: [
    { real_power: number, p: number},
    { real_power: number, p: number},
    { real_power: number, p: number},
  ]
  rms_voltage: [
    null | number,
    null | number,
    null | number,
  ]
}

export interface PortConfigEmHan {
  enabled: boolean
  to_summer_inverted: boolean
  driver: 'EM_HAN_DRIVER_HAN' | 'EM_HAN_DRIVER_RELAYED'
  uart_baudrate?: BaudRate
  uart_mode?: '8N1' | '8N2' | '8E1' | '8O1'
  uart_instance?: number
}

// Em 3-P

export interface PortValuesEm3Ph {
  energy: number
  power: number
  cur_current: [number, number, number]
  disconnected: [boolean, boolean, boolean, boolean]
  phase_sensor_signal_level: number
  current_level: [number, number, number]
  calib_status: [number, number, number]
  calibrating: boolean
  phases: [
    { real_power: number, p: number},
    { real_power: number, p: number},
    { real_power: number, p: number},
  ]
  freq: number
}

export type PortStatusEm3Ph = [] | ['IS_CALIBRATED']
export interface PortConfigEm3Ph {
  enabled: boolean
  enable_phase_sensing: boolean
  to_summer_inverted: boolean
  ma_per_v: [number, number, number]
  phase_shifts?: [number, number, number],
  calib_status?: PortStatusEm3Ph

}

// Em netsum
export interface PortValuesEmNetsum {
  energy: number
  power: number
  cur_current: [number, number, number]
  phases: [
    { real_power: number, p: number},
    { real_power: number, p: number},
    { real_power: number, p: number},
  ]
}

export interface PortConfigEmNetsum {
  enabled: boolean
}

// Energy storage

export interface PortValuesEnergyStorage {
  energy: number
  power: number
  received_command: number
  phase_curr: [number, number, number]
  phase_volt: [number, number, number]
  freq: number
  soc: number
  status: 'SPP_ENERGYSTORAGE_INFO_STATUS_OK'
    | 'SPP_ENERGYSTORAGE_INFO_STATUS_COMM_FAILURE'
    | 'SPP_ENERGYSTORAGE_INFO_STATUS_FAIL_PUBLIC_INTERNET' // Modbus TCP: Enion has a public/CGNAT IP, scan refused.
  extra: [unknown, unknown, unknown, unknown, unknown, unknown, unknown, unknown, unknown, unknown]
}

export type EnergyStorageFCRDriver =
  'SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE'
  | 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_HYBRID'
  | 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_S6'
  | 'SPP_ENERGYSTORAGE_DRIVER_GROWATT_HYBRID'
  | 'SPP_ENERGYSTORAGE_DRIVER_1KOMMA5'
  | 'SPP_ENERGYSTORAGE_DRIVER_DYNESS_DH200'
  | 'SPP_ENERGYSTORAGE_SUNGROW_HYBRID'
  | 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_EMMA'
  | 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_G4'
  | 'SPP_ENERGYSTORAGE_DRIVER_SIGENERGY'
  | 'SPP_ENERGYSTORAGE_DRIVER_SUNGROW_POWERSTACK'
export type EnergyStorageDriver =
  EnergyStorageFCRDriver
  | 'SPP_ENERGYSTORAGE_DRIVER_SUNSPEC'
  | 'SPP_ENERGYSTORAGE_DRIVER_GENERIC_MODBUS'
  | 'SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_HYBRID'
  | 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_HYBRID'
  | 'SPP_ENERGYSTORAGE_DRIVER_SOFAR_HYBRID'
  | 'SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_F2'
  | 'SPP_ENERGYSTORAGE_DRIVER_FRONIUS_GEN24'
  | 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_ALT'
  | 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_IES'

export type PortConfigEnergyStorageParams = PortConfigEnergyStorageSunspecParams | PortConfigEnergyStoragePlenticoreParams | PortConfigEnergyStorageSolisHybridParams | PortConfigEnergyStorageHuaweiHybridParams

export interface PortConfigEnergyStorage {
  enabled: boolean
  to_summer: boolean
  driver: EnergyStorageDriver
  params: PortConfigEnergyStorageParams
}

export interface PortConfigEnergyStorageSunspecParams {
  conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0' | 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_1' | 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP'
  conn_params: {
    baudrate: BaudRate,
    parity: Parity,
  }
  slave_id: number
  extras: [
    [number, number],
    [number, number],
    [number, number],
    [number, number],
    [number, number],
  ]
}

export type EnergyStorageExtras = [
  { func_code: number, reg_addr: number},
  { func_code: number, reg_addr: number},
  { func_code: number, reg_addr: number},
  { func_code: number, reg_addr: number},
  { func_code: number, reg_addr: number},
]

export interface PortConfigEnergyStoragePlenticoreParams {
  conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP'
  conn_params: {
    tcp_inst: number
    keep_socket: number
  }
  slave_id: number
  em_slave_id: number
  extras: EnergyStorageExtras
}

export interface PortConfigEnergyStorageSolisHybridParams {
  conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0' | 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_1'
  slave_id: number
  em_slave_id: number
  extras: EnergyStorageExtras
}

export interface PortConfigEnergyStorageHuaweiHybridParams {
  conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0' | 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_1' | 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP'
  // Only Modbus TCP carries conn_params (tcp_inst/keep_socket); the RS485 variants
  // of these drivers have no connection parameters at all (see connParamsForConnType).
  conn_params?: {
    tcp_inst: number
    keep_socket: number
  }
  slave_id: number
  em_slave_id: number
  extras: EnergyStorageExtras
}

// EVSE

export interface PortValuesEvse {
  configured_current_limit: number
  actual_current_limit: number
  measured_current: [number, number, number]
  status: number
  // Real-time charging power in W, or null if the driver doesn't report it (e.g. Charlie).
  charging_power: number | null
}

export type EvseDriver =
  | 'EVSE_DRIVER_GENERIC'
  | 'EVSE_DRIVER_CHARLIE_RS485'
  | 'EVSE_DRIVER_SUNGROW_MODBUS'
  | 'EVSE_DRIVER_FOXESS_MODBUS'

export interface PortConfigEvse {
  enabled: boolean
  driver: EvseDriver
  params: PortConfigEvseGenericParams | PortConfigEvseCharlieParams | PortConfigEvseSungrowParams |
    PortConfigEvseFoxessParams
}

export interface PortConfigEvseGenericParams {
  onoff_exists: boolean
  limit_exists: boolean
  onoff_invert: boolean
  limit_invert: boolean
  onoff_port: PortNumber
  limit_port: PortNumber
  nonlimited_current: number
  limited_current: number
}

export interface PortConfigEvseCharlieParams {
  rs485_inst: number
  slave_id: number
  max_current: number
}

export interface PortConfigEvseSungrowParams {
  modbus_inst: number
  slave_id: number
  max_current: number
}

/**
 * FoxESS AC EV charger (also sold as the 1KOMMA5 "Wallbox Smart" rebrand).
 * Mirrors spp_evse.h driver_foxess_modbus; every field round-trips through the server
 * codec, so the defaults must list them all or the read-back never equals the pending config.
 */
export interface PortConfigEvseFoxessParams {
  // 0, 1 = Modbus RTU on RS485 port 4/0, 4/1; 2 = Modbus TCP over the charger's LAN port.
  conn_type: number
  slave_id: number
  max_current: number
  baud_115200: boolean
  // false = control via Max Charging Power register, true = via Max Charging Current + start/stop.
  ctrl_current: boolean
  no_validity_write: boolean
  // TCP only: 0 = auto-scan the subnet, else connect to x.x.x.<this>.
  force_ip_last_octet: number
  tcp_port: number
  heartbeat_s: number
  validity_s: number
}

// Heat pump (port 0x23, spp_heatpump.h). Values decoded by server/lib/sunergos/protocol/ports/heat_pump.ex.

export type HeatPumpMode = 'HEATPUMP_MODE_OFF' | 'HEATPUMP_MODE_HEAT' | 'HEATPUMP_MODE_COOL' | 'HEATPUMP_MODE_DHW' | 'HEATPUMP_MODE_UNKNOWN'

export type HeatPumpStatus =
  'SPP_HEATPUMP_INFO_STATUS_OK' |
  'SPP_HEATPUMP_INFO_STATUS_COMM_FAILURE' |
  'SPP_HEATPUMP_INFO_STATUS_FAIL_PUBLIC_INTERNET' |
  'SPP_HEATPUMP_INFO_STATUS_FAULT' |
  'SPP_HEATPUMP_INFO_STATUS_UNKNOWN'

export type HeatPumpFlag = 'compressor_on' | 'defrost' | 'aux_space_on' | 'aux_dhw_on' | 'ext_source_on' | 'dhw_valve' |
  'disinfect' | 'pump_on' | 'silent' | 'curve_active' | 'antifreeze' | 'holiday' | 'under_control' | 'metering'

export type HeatPumpCap = 'power_target' | 'silent_cap' | 'compressor_direct' | 'setpoint_shift' | 'dhw' | 'dhw_force_hp' |
  'dhw_force_elec' | 'aux_force' | 'aux_prevent' | 'sg' | 'zone2' | 'elec_power' | 'thermal_power' | 'cool'

export type HeatPumpTemperature = 't_flow' | 't_return' | 't_outdoor' | 't_dhw_tank' | 't_buffer' | 't_room' |
  't_flow_zone2' | 't_flow_setpoint' | 't_dhw_setpoint'

export interface HeatPumpTracker {
  state: 'HEATPUMP_TRK_RELEASED' | 'HEATPUMP_TRK_ZONE_OFF' | 'HEATPUMP_TRK_STOP' | 'HEATPUMP_TRK_DOWN' | 'HEATPUMP_TRK_UP' |
    'HEATPUMP_TRK_FROZEN' | 'HEATPUMP_TRK_BLOCKED' | 'HEATPUMP_TRK_UNKNOWN'
  duty_cycling: boolean
  silent_cap: boolean
  source: 'HEATPUMP_TRK_SRC_NONE' | 'HEATPUMP_TRK_SRC_CMD' | 'HEATPUMP_TRK_SRC_OPTIMIZER' | 'HEATPUMP_TRK_SRC_UNKNOWN'
}

export type PortValuesHeatPump = {
  energy: number
  // W; null when not known.
  power: number | null
  thermal_power: number | null
  aux_power: number
  caps: Partial<Record<HeatPumpCap, boolean>>
  flags: Partial<Record<HeatPumpFlag, boolean>>
  sg_state: number
  mode: HeatPumpMode
  status: HeatPumpStatus
  fault_code: number
  compressor_hz: number | null
  compressor_target_hz: number | null
  // W the driver is tracking; null = released (the machine's own logic), 0 = space heating off
  target_power: number | null
  tracker: HeatPumpTracker
  rated_heat_power: number | null
  rated_aux_power: number
  machine_elec_energy: number | null
  machine_heat_energy: number | null
  modbus_cmd_status: number
  modbus_result: number[]
} & Record<HeatPumpTemperature, number | null>

export type HeatPumpDriver = 'HEATPUMP_DRIVER_NONE' | 'HEATPUMP_DRIVER_SG_READY_RELAYS' | 'HEATPUMP_DRIVER_MIDEA_MTHERMAL'

export type HeatPumpConnType = 'HEATPUMP_CONN_RTU_RS485_0' | 'HEATPUMP_CONN_RTU_RS485_1' | 'HEATPUMP_CONN_MODBUS_TCP'

/**
 * spp_heatpump_conf_t, as far as the server owns it: allow_silent_cap, the
 * retired cap/aux/preheat/write-interval fields and the reserved ones are always written as 0.
 * Every key must be listed in the defaults: the device read-back is compared
 * key by key against the pending config.
 */
export interface PortConfigHeatPump {
  enabled: boolean
  driver: HeatPumpDriver
  conn_type: HeatPumpConnType
  slave_id: number
  baud_sel: number
  parity: number
  force_ip_last_octet: number
  failsafe_release_minutes: number
  cmd_valid_minutes: number
  allow_setpoint: boolean
  allow_zone_off: boolean
  allow_dhw_control: boolean
  // degC, 0 = firmware default (20 / 45)
  flow_setpoint_min: number
  flow_setpoint_max: number
  dhw_setpoint_normal: number
  dhw_setpoint_boost: number
  // W, multiples of 10; 0 = from the machine
  rated_elec_power: number
  rated_aux_power: number
  poll_interval_s: number
  zone_mask: number
}

// Fw Info

export interface PortValuesFwInfo {
  seconds_since_boot: number
  flag_conf_a_valid: boolean
  flag_conf_b_in_use: boolean
  flag_conf_b_valid: boolean
  flag_dfu_error: boolean
  flag_dfu_started: boolean
  flag_fw_a_valid: boolean
  flag_fw_b_in_use: boolean
  flag_fw_b_valid: boolean
  fw_a_crc?: string
  fw_a_update_cnt: number
  fw_a_version: string
  fw_b_crc?: string
  fw_b_update_cnt: number
  fw_b_version: string
  dfu_first_missing_chunk: number
  flag_new_protocol?: boolean
  dfu_n_received_chunks?: number
  dfu_n_chunks?: number
}

export interface PortConfigFwInfo {
}

// Relay

export interface PortValuesRelay {
  is_controllable: boolean
  is_on: boolean
}

export interface PortConfigRelay {
  enabled: boolean
  invert: boolean
  failsafe_enable: boolean
  failsafe_state: boolean
}

export interface PortCommandRelay {
  name: 'is_on'
  value: boolean
}

// Solar

export type PortValuesSolar = {
  energy: number
  power: number
} | {
  energy: number
  power: number
  status: string
  state: string
  all_time_wh: number
  phase_curr: [number, number, number]
  phase_volt: [number, number, number]
  freq: number
  extra: [unknown, unknown, unknown, unknown, unknown, unknown, unknown, unknown, unknown, unknown]
}

export interface PortConfigSolarSunspcec {
  enabled: boolean
  to_summer: boolean
  driver: 'SPP_SOLAR_DRIVER_SUNSPEC'
  params: SolarPortSunspecConfigParams
}

export interface PortConfigSolarStegagrid {
  enabled: boolean
  to_summer: boolean
  driver: 'SPP_SOLAR_DRIVER_STECAGRID'
  params: SolarPortStecaGridConfigParams
}

export interface PortConfigSolarSolis {
  enabled: boolean
  to_summer: boolean
  driver: 'SPP_SOLAR_DRIVER_SOLIS'
  params: SolarPortSolisConfigParams
}

export interface PortConfigSolarSofar {
  enabled: boolean
  to_summer: boolean
  driver: 'SPP_SOLAR_DRIVER_SOFAR_G3' | 'SPP_SOLAR_DRIVER_SOFAR_G2'
  params: SolarPortSolisConfigParams
}

export interface PortConfigSolarRelayed {
  enabled: boolean
  to_summer: boolean
  driver: 'SPP_SOLAR_DRIVER_RELAYED'
  params: SolarPortRelayedConfigParams
}

export type PortConfigSolar = PortConfigSolarSunspcec | PortConfigSolarStegagrid | PortConfigSolarSolis | PortConfigSolarSofar | PortConfigSolarRelayed

// Temperature.

export interface PortValuesTemperature {
  temperature: number
  error?: boolean
}

export type TemperatureDriver = 'TEMPERATURE_DRIVER_CPU' | 'TEMPERATURE_DRIVER_DS18B20' | 'TEMPERATURE_DRIVER_NTC' | 'TEMPERATURE_DRIVER_OTHERPORT'

export interface PortConfigTemperatureCPU {
  driver: 'TEMPERATURE_DRIVER_CPU'
  params: {
    calib_offset: number
    disable_dynamic_compensation: boolean
  }
}

export interface PortConfigTemperatureDS18B20 {
  driver: 'TEMPERATURE_DRIVER_DS18B20'
  params: {
    instance: number
    port: number
  }
}

export interface PortConfigTemperatureNTCParams {
  r_at_25: number
  b_value: number
  calib_lut: [number, number, number, number]
}

export interface PortConfigTemperatureNTC {
  driver: 'TEMPERATURE_DRIVER_NTC'
  params: PortConfigTemperatureNTCParams
}

export interface PortConfigTemperatureOherPortParams {
  instance: number
  port: number
  id: number
}

export interface PortConfigTemperatureOherPort {
  driver: 'TEMPERATURE_DRIVER_OTHERPORT'
  params: PortConfigTemperatureOherPortParams
}

export type PortConfigTemperature = (PortConfigTemperatureCPU | PortConfigTemperatureDS18B20 | PortConfigTemperatureNTC | PortConfigTemperatureOherPort) & {
  enabled: boolean
}

// Optimizer
export interface PortValuesOptimizer {
}

export interface PortDestinationKind {
  dest_kind: DestinationKind
  dest: number
  port: PortNumber
}

export type MultioptRole = 'MULTIOPT_NO' | 'MULTIOPT_LEADER' | 'MULTIOPT_FOLLOWER' | 'MULTIOPT_RESERVED'

export interface PortConfigOptimizer {
  enabled: boolean
  enable_fuse_protection: boolean
  enable_power_limit: boolean
  multiopt_role: MultioptRole
  multiopt_group_id: number
  current_limits: [number, number, number],
  fuse_curve: [number, number, number, number, number]
  // Tehomaksu: with peak_price > 0, the free power (W) when enable_power_limit is set.
  power_limit: number
  // Not used by the firmware any more; kept on the wire.
  power_limit_averaging_period: number,
  // Tehomaksu (power fee), see PowerFeeForm: c/kW/month (0 = off), local hours [from, to)
  // (equal = all day), bit0 = Monday, months a peak stays billed (0/1 = calendar month,
  // 12 = ratchet), bit0 = January; a zero mask means all.
  peak_price: number
  peak_hour_from: number
  peak_hour_to: number
  peak_weekday_mask: number
  peak_lookback_months: number
  peak_month_mask: number
}

export type RelayReason =
  'REASON_FORCE' |
  'REASON_PRICE' |
  'REASON_REALTIME_SELL_AVOIDANCE' |
  'REASON_PREDICTED_SELL_AVOIDANCE' |
  'REASON_FUSE_PROTECTION' |
  'REASON_COMPENSATION_SELL_AVOIDANCE' |
  'REASON_ENION_CLOUD' |
  'REASON_UTILITY' |
  'REASON_CALENDAR' |
  'REASON_TEMPERATURE_LIMIT' |
  'REASON_TEMPERATURE_LIMIT_HARD' |
  'REASON_END_OF_PREVIOUS_ITEM' |
  'REASON_DEFAULT' |
  // Wire code the protocol does not define; the server decodes it instead of dropping the frame.
  'REASON_UNKNOWN'

export type OptimizerCommandValue = 0 | 15 | 30 | 45 | 60 | 70 | 85 | 100

export interface OptimizerCommand {
  name: 'is_on'
  value: OptimizerCommandValue
  reason: RelayReason
}

export type OptimizerSchedule = [TimeString, OptimizerCommand][]

export interface PortValuesOptimizerControl {
  port: PortNumber
  hint_time_constant: number
  hint_duty: number
  plan_between_state:
    'PLAN_BETWEEN_STATE_NO' | 'PLAN_BETWEEN_STATE_PENDING' | 'PLAN_BETWEEN_STATE_PLANNED' | 'PLAN_BETWEEN_STATE_UNKNOWN'
  schedule: OptimizerSchedule
}

export interface PortConfigOptimizerControl {
  use_temperature_meas: boolean
  use_clock_based_duty: boolean
  use_fixed_price: boolean
  rapid_switching_ok: boolean
  good_sink: boolean
  enabled: boolean

  cont_dest: number
  cont_dest_kind: DestinationKind

  port: PortNumber

  fixed_buy_price_threshold: number

  min_duty?: number
  max_duty?:number
  min_duty_at?: number
  max_duty_at?: number
  hourly_fixed_duties?: [number, number, number, number, number, number, number, number]

  time_constant: number

  tempsens_dest: number
  tempsens_dest_kind: DestinationKind
  tempsens_port: PortNumber

  min_temp: number
  abs_min_temp: number
  max_temp: number
  abs_max_temp: number

  cop_curve: [number, number, number, number]

  pv_priority: number
  plan_by_price: boolean

  fuse_priority: number
  phases: [boolean, boolean, boolean]

  appliance_power: number
}

export interface PortValuesOptimizerPrice {
  base_ts: TimeString
  timestep: number
  prices: number[]
}

export interface PortConfigOptimizerPrice {
}

export type Cloudiness = 'CLOUDY' | 'PARTLY CLOUDY' | 'SCATTERED' | 'SUNNY'
export type WindDir = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW'
export interface Weather {
  sun: Cloudiness
  temperature: number
  wind_dir: WindDir
  wind_speed: number
}

export interface PortValuesOptimizerWeather {
  base_ts: TimeString
  timestep: number
  weathers: Weather[]
}

export interface PortConfigOptimizerWeather {
}

// Radio
export interface PortValuesRadio {

}

export type RadioMode = 'RADIO_MODE_SLAVE' | 'RADIO_MODE_MASTER' | 'RADIO_MODE_DISABLED'

export interface PortConfigRadio {
  radio_mode: RadioMode
  slave_devids?: [
    DeviceHwID | null,
    DeviceHwID | null,
    DeviceHwID | null,
    DeviceHwID | null,
    DeviceHwID | null,
    DeviceHwID | null
  ]
}

// General definitions.

export type PortValues = PortValuesBatteryOptimizer |
  PortValuesBatteryOptimizer2 |
  PortValuesBatteryOptimizerSocPlan |
  PortValuesClock |
  PortValuesEmSimple |
  PortValuesEm3Ph |
  PortValuesEmHan |
  PortValuesEmNetsum |
  PortValuesEnergyStorage |
  PortValuesEvse |
  PortValuesFwInfo |
  PortValuesHeatPump |
  PortValuesRelay |
  PortValuesSolar |
  PortValuesTemperature |
  PortValuesOptimizer |
  PortValuesRadio

export type PortValueName = keyof PortValuesBatteryOptimizer |
  keyof PortValuesBatteryOptimizer2 |
  keyof PortValuesBatteryOptimizerSocPlan |
  keyof PortValuesClock |
  keyof PortValuesEmSimple |
  keyof PortValuesEmHan |
  keyof PortValuesEmNetsum |
  keyof PortValuesEnergyStorage |
  keyof PortValuesEvse |
  keyof PortValuesFwInfo |
  keyof PortValuesHeatPump |
  keyof PortValuesRelay |
  keyof PortValuesSolar |
  keyof PortValuesTemperature |
  keyof PortValuesRadio

export type PortConfig = PortConfigBatteryOptimizer |
  PortConfigBatteryOptimizer2 |
  PortConfigBatteryOptimizerSocPlan |
  PortConfigClock |
  PortConfigEmSimple |
  PortConfigEm3Ph |
  PortConfigEmHan |
  PortConfigEmNetsum |
  PortConfigEnergyStorage |
  PortConfigEvse |
  PortConfigFwInfo |
  PortConfigHeatPump |
  PortConfigRelay |
  PortConfigSolarSunspcec |
  PortConfigSolarStegagrid |
  PortConfigSolarSolis |
  PortConfigSolar |
  PortConfigTemperature |
  PortConfigOptimizer |
  PortConfigRadio

export type FullConfig<ConfigType extends PortConfig> = ConfigType & { pending?: Partial<ConfigType> } & { hardware?: ConfigType }

export type UncommittedConfig = {
  id: ID,
  port: PortNumber,
  config: PortConfig,
}

// Port definitions.

export type BatteryOptimizerConfig = FullConfig<PortConfigBatteryOptimizer>
export interface BatteryOptimizerPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: BatteryOptimizerConfig
  values: PortValuesBatteryOptimizer
}
export type BatteryOptimizer2Config = FullConfig<PortConfigBatteryOptimizer2>
export interface BatteryOptimizer2Port extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: BatteryOptimizer2Config
  values: PortValuesBatteryOptimizer2
}
export type BatteryOptimizerSocPlanConfig = FullConfig<PortConfigBatteryOptimizerSocPlan>
export interface BatteryOptimizerSocPlanPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: BatteryOptimizerSocPlanConfig
  values: PortValuesBatteryOptimizerSocPlan
}
export type ClockConfig = FullConfig<PortConfigClock>
export interface ClockPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: ClockConfig
  values: PortValuesClock
}
export type EmSimpleConfig = FullConfig<PortConfigEmSimple>
export interface EmSimplePort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: EmSimpleConfig
  values: PortValuesEmSimple
}
export type Em3PhConfig = FullConfig<PortConfigEm3Ph>
export interface Em3PhPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: Em3PhConfig
  values: PortValuesEm3Ph
}
export type EmHanConfig = FullConfig<PortConfigEmHan>
export interface EmHanPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: EmHanConfig
  values: PortValuesEmHan
}
export type EmNetsumConfig = FullConfig<PortConfigEmNetsum>
export interface EmNetsumPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: EmNetsumConfig
  values: PortValuesEmNetsum
}
export type PowerMeterPort = Em3PhPort | EmSimplePort | EmHanPort | EmNetsumPort
export type EnergyStorageConfig = FullConfig<PortConfigEnergyStorage>
export interface EnergyStoragePort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: EnergyStorageConfig
  values: PortValuesEnergyStorage
}
export type EvseConfig = FullConfig<PortConfigEvse>
export interface EvsePort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: EvseConfig
  values: PortValuesEvse
}
export type FwInfoConfig = FullConfig<PortConfigFwInfo>
export interface FwInfoPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: FwInfoConfig
  values: PortValuesFwInfo
}
export type HeatPumpConfig = FullConfig<PortConfigHeatPump>
export interface HeatPumpPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: HeatPumpConfig
  values: PortValuesHeatPump
}
export type RelayConfig = FullConfig<PortConfigRelay>
export interface RelayPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: RelayConfig
  values: PortValuesRelay
}
export type SolarConfig = FullConfig<PortConfigSolar>
export interface SolarPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: SolarConfig
  values: PortValuesSolar
}
export type TemperatureConfig = FullConfig<PortConfigTemperature>
export interface TemperaturePort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: TemperatureConfig
  values: PortValuesTemperature
}
export type OptimizerConfig = FullConfig<PortConfigOptimizer>
export interface OptimizerPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: OptimizerConfig
  values: PortValuesOptimizer
}
export type OptimizerControlConfig = FullConfig<PortConfigOptimizerControl>
export interface OptimizerControlPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: OptimizerControlConfig
  values: PortValuesOptimizerControl
}
export type OptimizerPriceConfig = FullConfig<PortConfigOptimizerPrice>
export interface OptimizerPricePort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: OptimizerPriceConfig
  values: PortValuesOptimizerPrice
}
export type OptimizerWeatherConfig = FullConfig<PortConfigOptimizerWeather>
export interface OptimizerWeatherPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: OptimizerWeatherConfig
  values: PortValuesOptimizerWeather
}
export type RadioConfig = FullConfig<PortConfigRadio>
export interface RadioPort extends Omit<Omit<Port, 'values'>, 'config'>
{
  config: RadioConfig
  values: PortValuesRadio
}

// Port type checkers

export function isClockBatteryOptimizer(obj: unknown): obj is BatteryOptimizerPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'BATTERY_OPTIMIZER'
}
export function isClockPort(obj: unknown): obj is ClockPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'CLOCK'
}
export function isEmSimplePort(obj: unknown): obj is EmSimplePort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'EM_SIMPLE'
}
export function isEm3PhPort(obj: unknown): obj is Em3PhPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'EM_3PH'
}
export function isEmHanPort(obj: unknown): obj is EmHanPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'EM_HAN'
}
export function isEmNetsumPort(obj: unknown): obj is EmNetsumPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'EM_NETSUM'
}
export function isPowerMeterPort(obj: unknown): obj is Em3PhPort {
  return isEm3PhPort(obj) || isEmSimplePort(obj) || isEmHanPort(obj)
}
export function isEnergyStoragePort(obj: unknown): obj is EnergyStoragePort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'ENERGY_STORAGE'
}
export function isEvsePort(obj: unknown): obj is EvsePort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'EVSE'
}
export function isFwInfoPort(obj: unknown): obj is FwInfoPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'FW_INFO'
}
export function isHeatPumpPort(obj: unknown): obj is HeatPumpPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'HEAT_PUMP'
}
export function isRelayPort(obj: unknown): obj is RelayPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'RELAY'
}
export function isSolarPort(obj: unknown): obj is SolarPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'SOLAR'
}
export function isTemperaturePort(obj: unknown): obj is TemperaturePort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'TEMPERATURE'
}
export function isOptimizerPort(obj: unknown): obj is OptimizerPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'OPTIMIZER'
}
export function isOptimizerControlPort(obj: unknown): obj is OptimizerControlPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'OPTIMIZER_CONTROL'
}
export function isOptimizerPricePort(obj: unknown): obj is OptimizerPricePort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'OPTIMIZER_PRICE'
}
export function isOptimizerWeatherPort(obj: unknown): obj is OptimizerWeatherPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'OPTIMIZER_WEATHER'
}
export function isRadioPort(obj: unknown): obj is RadioPort {
  return typeof obj === 'object' && obj !== null && (obj as any).type === 'RADIO'
}

// Config diff

export type PortConfigDiffTypeGood = 'OK' | 'DISABLED' | 'FUTURE'
export type PortConfigDiffTypeBad = 'DIFFERS' | 'READ FAILED' | 'MISSING'
export type PortConfigDiffType = PortConfigDiffTypeGood | PortConfigDiffTypeBad

export function isPortConfigDiffTypeBad(obj: unknown): obj is PortConfigDiffTypeBad {
  return typeof obj === 'object' && obj !== null && ((obj as any).type === 'DIFFERS' || (obj as any).type === 'READ FAILED' || (obj as any).type === 'MISSING')
}

export type PortConfigDiff = {
  id: ID
  name: string
  type: 'OK' | 'READ FAILED' | 'MISSING' | 'DISABLED' | 'FUTURE'
} | {
  id: ID
  name: string
  type: 'DIFFERS'
  hardware: Record<string, unknown>
  config: Record<string, unknown>
}

export type ConfigDiff = Record<PortNumber, PortConfigDiff>
